import { UserCallback, InitArguments } from 'src/types';

import { MD5 } from './md5';

const OK_CONNECT_URL = 'https://connect.ok.ru/';
const OK_MOB_URL = 'https://m.ok.ru/';
const OK_API_SERVER = 'https://api.ok.ru/';
const OK_WEB_SERVER = 'https://ok.ru/';

const OK_ANDROID_APP_UA = 'OkApp';

const MOBILE = 'mobile';
const WEB = 'web';
const NATIVE_APP = 'application';
const EXTERNAL = 'external';
const SERVICE_EVENT = 'serviceEvent';

const LIGHT_THEME = '__light-theme';
const DARK_THEME = '__dark-theme';
const THEME_ENABLED = '__ui-theme_enabled';

const PLATFORM_REGISTER: Record<string, string> = {
    w: WEB,
    m: MOBILE,
    n: NATIVE_APP,
    e: EXTERNAL
};

const state: Record<string, any> = {
    app_id: '0',
    app_key: '',
    sessionKey: '',
    accessToken: '',
    sessionSecretKey: '',
    apiServer: '',
    webServer: '',
    widgetServer: '',
    mobServer: '',
    baseUrl: '',
    container: false,
    header_widget: ''
};

let sdk_success = nop;
let sdk_failure = nop;
let eventCallback = nop;

// ---------------------------------------------------------------------------------------------------
// General
// ---------------------------------------------------------------------------------------------------

/**
 * initializes the SDK<br/>
 * If launch parameters are not detected, switches to OAUTH (via redirect)
 *
 * @param args
 * @param {Number} args.app_id application id
 * @param {String} args.app_key application key
 * @param [args.oauth] - OAUTH configuration
 * @param {String} [args.oauth.scope='VALUABLE_ACCESS'] scope
 * @param {String} [args.oauth.url=location.href] return url
 * @param {String} [args.oauth.state=''] state for security checking
 * @param {String} [args.oauth.layout='a'] authorization layout (w - web, m - mobile)
 * @param {Function} success success callback
 * @param {Function} failure failure callback
 */

function init(args: InitArguments, success?: typeof nop, failure?: (error: string) => void) {
    args.oauth = args.oauth || {};
    sdk_success = isFunc(success) ? success! : nop;
    sdk_failure = isFunc(failure) ? failure! : nop;

    const params = getRequestParameters(args.location_search || window.location.search);
    const hParams = getRequestParameters(args.location_hash || window.location.hash);

    state.app_id = args.app_id;
    state.app_key = params.application_key || args.app_key;

    if (!state.app_id || !state.app_key) {
        sdk_failure('Required arguments app_id/app_key not passed');
        return;
    }

    state.sessionKey = params.session_key;
    state.accessToken = hParams.access_token;
    state.groupId = params.group_id || hParams.group_id || args.group_id;
    state.sessionSecretKey = params.session_secret_key || hParams.session_secret_key;
    state.apiServer = args.api_server || params.api_server || OK_API_SERVER;
    state.webServer = args.web_server || params.web_server || OK_WEB_SERVER;
    state.widgetServer = encodeURI(getRemoteUrl([args.widget_server!, params.widget_server], OK_CONNECT_URL));
    state.mobServer = encodeURI(getRemoteUrl([args.mob_server!, params.mob_server], OK_MOB_URL));
    state.baseUrl = state.apiServer + 'fb.do';
    state.header_widget = params.header_widget;
    state.container = params.container;
    state.layout =
        params.layout || hParams.layout || args.layout || params.api_server
            ? params.mob && params.mob === 'true'
                ? 'm'
                : 'w'
            : 'w';

    if (!params.api_server) {
        if (hParams.access_token == null && hParams.error == null) {
            //@ts-ignore
            window.location =
                state.widgetServer +
                'oauth/authorize' +
                '?client_id=' +
                args.app_id +
                '&scope=' +
                (args.oauth.scope || 'VALUABLE_ACCESS') +
                '&response_type=' +
                'token' +
                '&redirect_uri=' +
                (args.oauth.url || window.location.href) +
                '&layout=' +
                (args.oauth.layout || 'a') +
                '&state=' +
                (args.oauth.state || '');
            return;
        }

        if (hParams.error != null) {
            sdk_failure('Error with OAUTH authorization: ' + hParams.error);
            return;
        }
    }

    window.addEventListener('message', onWindowMessage);
    sdk_success();
}

function serviceEventHandler(eventData: string) {
    var serviceArgs = eventData.split(':');
    switch (serviceArgs[0]) {
        case 'DEVICE_THEME':
            document.documentElement.classList.toggle(LIGHT_THEME, serviceArgs[1] === 'light');
            document.documentElement.classList.toggle(DARK_THEME, serviceArgs[1] === 'dark');
            document.documentElement.classList.toggle(THEME_ENABLED, serviceArgs[1] === 'auto');
            break;
    }
}

function onWindowMessage(event: MessageEvent) {
    if (event.origin === state.mobServer || event.origin === state.apiServer || event.origin === state.webServer) {
        let args = event.data.split('$', -1);
        if (args.length < 3) return;
        args = args.map(decodeURIComponent);
        let method = args[0];
        if (method === SERVICE_EVENT) {
            serviceEventHandler(args[2]);
        }
        if (method.startsWith('__FAPI__')) method = method.slice('__FAPI__'.length);
        eventCallback(method, args[1], args[2]);
    }
}

/**
 * @param {Array} sources
 * @param {String} fallback
 */
function getRemoteUrl(sources: string[], fallback: string) {
    for (const source of sources) {
        if (source && (source.startsWith('http://') || source.startsWith('https://'))) return getBaseUrl(source);
    }

    return fallback;
}

function getBaseUrl(stringUrl: string) {
    // URL() not supported for Android < 4.4
    const url = document.createElement('a');
    url.setAttribute('href', stringUrl);
    return url.protocol + '//' + url.hostname + '/';
}

// ---------------------------------------------------------------------------------------------------
// REST
// ---------------------------------------------------------------------------------------------------

const REST_NO_SIGN_ARGS = new Set(['sig', 'access_token']);

function executeRemoteRequest(query: string, usePost: boolean, callback: UserCallback) {
    const xhr = new XMLHttpRequest();
    if (usePost) {
        xhr.open('POST', state.baseUrl, true);
        xhr.setRequestHeader('Content-type', 'application/x-www-form-urlencoded');
    } else {
        xhr.open('GET', state.baseUrl + '?' + query, true);
        xhr.setRequestHeader('Content-type', 'application/json');
    }

    xhr.onreadystatechange = function () {
        if (xhr.readyState === XMLHttpRequest.DONE) {
            if (!isFunc(callback)) return;
            let responseJson;
            try {
                responseJson = JSON.parse(xhr.responseText);
            } catch {
                responseJson = { result: xhr.responseText };
            }

            if (xhr.status != 200 || responseJson.hasOwnProperty('error_msg')) {
                callback('error', null, responseJson);
            } else {
                callback('ok', responseJson, null);
            }
        }
    };

    xhr.send(usePost ? query : null);
}

/**
 * Calls a REST request
 *
 * @param {String} method
 * @param {Object} [params]
 * @param {restCallback} [callback]
 * @param {Object} [callOpts]
 * @param {boolean} [callOpts.no_session] true if REST method prohibits session
 * @param {boolean} [callOpts.no_sig] true if no signature is required for the method
 * @param {string} [callOpts.app_secret_key] required for non-session requests
 * @param {string} [callOpts.use_post] send request via POST
 */

interface ICallOpts {
    no_session?: boolean;
    no_sig?: boolean;
    app_secret_key?: string;
    use_post?: boolean;
}

function restCall(method: string, params: Record<string, any>, callback: UserCallback, callOpts: ICallOpts) {
    params = params || {};
    params.method = method;
    params = restFillParams(params);
    if (callOpts && callOpts.no_session) {
        delete params.session_key;
        delete params.access_token;
    }

    let key;
    for (key in params) {
        if (params.hasOwnProperty(key)) {
            const param = params[key];
            if (typeof param === 'object') {
                params[key] = JSON.stringify(param);
            }
        }
    }

    if (!callOpts || !callOpts.no_sig) {
        const secret = callOpts && callOpts.app_secret_key ? callOpts.app_secret_key : state.sessionSecretKey;
        params.sig = calcSignature(params, secret);
    }

    let query = '';
    for (key in params) {
        if (params.hasOwnProperty(key)) {
            if (query.length !== 0) query += '&';
            query += key + '=' + encodeURIComponent(params[key]);
        }
    }

    return executeRemoteRequest(query, !!callOpts?.use_post, callback);
}

function calcSignature(query: Record<string, any>, secretKey: string) {
    let i;
    const keys = [];
    for (i in query) {
        /* eslint "no-prototype-builtins": "off" */
        if (query.hasOwnProperty(i)) {
            keys.push(i.toString());
        }
    }

    keys.sort();
    let sign = '';
    for (i = 0; i < keys.length; i++) {
        const key = keys[i];
        if (!REST_NO_SIGN_ARGS.has(key)) {
            sign += keys[i] + '=' + query[keys[i]];
        }
    }

    sign += secretKey || state.sessionSecretKey;
    sign = encodeUtf8(sign);
    return MD5.instance.calc(sign);
}

function restFillParams(params: Record<string, any>) {
    params = params || {};
    params.application_key = state.app_key;
    if (state.sessionKey) {
        params.session_key = state.sessionKey;
    } else {
        params.access_token = state.accessToken;
    }

    params.format = 'JSON';
    return params;
}

function createAppExternalLink(href: string) {
    if (state.context.isOKApp) {
        return (state.context.isIOS ? 'apphook:applink:' : 'https://ok.ru/apphook/outlink?url=') + href;
    }

    return href;
}

function getInnerType(object: any) {
    return Object.prototype.toString.call(object);
}

getInnerType._object = getInnerType({}); // [object Object]
getInnerType._function = getInnerType(nop); // [object Function]
getInnerType._array = getInnerType([]); // [object Array]
getInnerType._string = getInnerType(''); // [object String]
getInnerType._number = getInnerType(1); // [object Number]

function isFunc(object: any) {
    return getInnerType(object) === getInnerType._function;
}

/**
 * Parses parameters to a JS map<br/>
 * Supports both window.location.search and window.location.hash)
 * @param {String} [source=window.location.search] string to parse
 * @returns {Object}
 */
function getRequestParameters(source?: string) {
    const res = {} as Record<string, string>;
    let url = source || window.location.search;
    if (url) {
        url = url.slice(1); // Drop the leading '?' / '#'
        const nameValues = url.split('&');

        for (const nameValue_ of nameValues) {
            const nameValue = nameValue_.split('=');
            const name = nameValue[0];
            let value = nameValue[1];
            value = value && decodeURIComponent(value.replace(/\+/g, ' '));
            res[name] = value;
        }
    }

    return res;
}

function encodeUtf8(string: string) {
    return unescape(encodeURIComponent(string));
}
/**
 * Checks if a game was opened in OK Android app's WebView
 * Checks if a game is opened in an OK Android app's WebView
 */
function isLaunchedInOKAndroidWebView() {
    const userAgent = window.navigator.userAgent;

    return userAgent && userAgent.length >= 0 && userAgent.includes(OK_ANDROID_APP_UA);
}

/** Stub func */
function nop(..._args: any[]) {}

function resolveContext() {
    const stateMode = state.layout && state.layout.toLowerCase();
    const userAgent = navigator.userAgent.toLowerCase();
    const IOS_UA_REG = /(iphone|ipad|ipod)/g;
    const ANDROID_UA_REG = /android/g;
    const WP_UA_REG = /windows phone/g;
    const context: Record<string, any> = {
        layout: PLATFORM_REGISTER[stateMode],
        isOKApp: Boolean(state.container),
        isOAuth: stateMode === 'o',
        isIframe: window.parent && window.parent !== window,
        isPopup: window.opener && window.opener !== window,
        isAndroid: ANDROID_UA_REG.test(userAgent),
        isIOS: IOS_UA_REG.test(userAgent),
        isWP: WP_UA_REG.test(userAgent)
    };
    context.isExternal = context.layout == EXTERNAL || !(context.isIframe || context.isPopup || context.isOAuth);
    context.isMob = context.layout === MOBILE || context.layout === NATIVE_APP;

    return (state.context = context);
}

export const Util = {
    getRequestParameters,
    isLaunchedFromOKApp: isLaunchedInOKAndroidWebView,
    openAppExternalLink(href: string) {
        if ((state.context || resolveContext()).isOKApp) {
            return location.assign(createAppExternalLink(href));
        }

        return window.open(createAppExternalLink(href));
    }
};

const postMethods = {
    'appsdata.update': 1,
    'appext.broadcastGetPreviewUrl': 1,
    'appext.broadcastScheduleMessage': 1,
    'photoContest.createCompetition': 1,
    'photoContest.editCompetition': 1,
    'photosV2.commit': 1,
    'photos.commitClones': 1,
    'mediatopic.post': 1,
    'application.setSettings': 1,
    'daily_dragon.openDragon': 1,
    'daily_dragon.openTicket': 1,
    'gamification.missionStateChanged': 1,
    'log.externalGamingLog': 1,
    'banners.closeBanner': 1,
    // 'game.act': 1,
    'snake_2025.openLootbox': 1,
    'snake_2025.selectSnake': 1,
    'snake_2025.exchangeSnake': 1,
    'snake_2025.rollRevolver': 1,
    'storage.set': 1
};

interface ErrorInfo {
    error_code: string;
    error_msg: string;
    error_data: Record<string, string>;
}

function callRemoteApi<T>(parameters: Record<string, any>) {
    return new Promise<T & Partial<ErrorInfo>>((resolve, reject) => {
        restCall(
            parameters.method,
            parameters,
            (status, data, error) => {
                if (status === 'error') {
                    console.log(data, error);

                    reject(error);
                } else {
                    resolve(data);
                }
            },
            {
                use_post: parameters.method in postMethods
            }
        );
    });
}

export const api = {
    call: callRemoteApi,
    init
};
